import type { ReactNode } from 'react'
import type { IntakeBatch } from '@adc/sdk/types'

/**
 * Columns that can be conditionally shown/hidden based on user role.
 * Admin-only columns: records, success, errors, coverageScore, structuralScore.
 */
export enum IntakeFileHistoryColumn {
  Organization = 'organization',
  BatchId = 'batchId',
  IngestionId = 'ingestionId',
  FileName = 'fileName',
  Classification = 'classification',
  UploadDate = 'uploadDate',
  UploadedBy = 'uploadedBy',
  Status = 'status',
  Records = 'records',
  Success = 'success',
  Errors = 'errors',
  CoverageScore = 'coverageScore',
  StructuralScore = 'structuralScore',
  Mapping = 'mapping',
  Actions = 'actions',
}

/**
 * All columns shown by default (admin view).
 *
 * Derived from the enum so new columns are picked up automatically, minus
 * `Organization` — an opt-in, cross-org column (only meaningful when a table
 * aggregates batches across organizations, e.g. the ADC org file-upload tab) —
 * and `Actions`, an opt-in column that only renders when the consumer wires an
 * `onReprocess` handler. Consumers add either explicitly.
 */
export const ALL_COLUMNS: IntakeFileHistoryColumn[] = (
  Object.values(IntakeFileHistoryColumn) as IntakeFileHistoryColumn[]
).filter(
  col =>
    col !== IntakeFileHistoryColumn.Organization &&
    col !== IntakeFileHistoryColumn.Actions
)

/** Non-admin column set (hides IDs, records/success/errors/scores) */
export const STANDARD_COLUMNS: IntakeFileHistoryColumn[] = [
  IntakeFileHistoryColumn.FileName,
  IntakeFileHistoryColumn.Classification,
  IntakeFileHistoryColumn.UploadDate,
  IntakeFileHistoryColumn.UploadedBy,
  IntakeFileHistoryColumn.Status,
]

/**
 * Per-column cell override. Pass an object (instead of the bare enum) in
 * `visibleColumns` to replace that column's default cell body with custom
 * content — a link, badge, click handler, custom styling, anything. The DS
 * still owns the surrounding cell chrome (header, tooltip, cell wrapper).
 *
 * `render` runs per row and receives the batch + row index; return a string
 * for plain text or any ReactNode for custom markup.
 */
export interface IntakeColumnOverride {
  column: IntakeFileHistoryColumn
  render: (batch: IntakeBatch, index: number) => ReactNode
}

/**
 * A column entry: either the bare enum (default rendering) or an override
 * object. `IntakeFileHistoryColumn[]` is assignable to `IntakeColumnConfig[]`,
 * so existing `ALL_COLUMNS` / `STANDARD_COLUMNS` call sites are unchanged.
 */
export type IntakeColumnConfig = IntakeFileHistoryColumn | IntakeColumnOverride

export interface IntakeFileHistoryTableProps {
  /** Array of intake batch rows to display */
  rows: IntakeBatch[]
  /** Whether data is currently loading */
  isLoading: boolean
  /** Total row count for pagination */
  totalCount: number
  /** Current page number (1-indexed) */
  currentPage: number
  /** Page size for pagination calculations */
  pageSize?: number
  /** Callback when page changes */
  onPageChange: (page: number) => void
  /**
   * Which columns to render, in the component's canonical order. Use
   * STANDARD_COLUMNS for non-admin, ALL_COLUMNS for admin. Each entry is either
   * the bare `IntakeFileHistoryColumn` (default cell rendering) or an
   * `IntakeColumnOverride` `{ column, render }` to supply custom cell content
   * (link, badge, click handler, etc.) while keeping the DS cell chrome.
   */
  visibleColumns?: IntakeColumnConfig[]
  /** Current search text value (controlled) */
  searchText?: string
  /** Callback when search text changes. If omitted, search is hidden. */
  onSearchTextChange?: (text: string) => void
  /** Search input placeholder */
  searchPlaceholder?: string
  /** Filter data from @adc/blueprint filters.hooks.useFilters(). If omitted, filters are hidden. */
  filterData?: any
  /** Filter actions from @adc/blueprint filters.hooks.useFilters() */
  filterActions?: any
  /** Enable expandable row details showing results and output files. Default: false */
  expandable?: boolean
  /** Callback when refresh button is clicked. If omitted, refresh button is hidden. */
  onRefresh?: () => void
  /**
   * Callback when a row's Reprocess button is clicked. Enables the `Actions`
   * column: a Reprocess button renders per row, gated on `batch.is_reprocessable`.
   * The consumer owns confirmation + list refresh. If omitted, the Actions
   * column renders nothing even when present in `visibleColumns`.
   */
  onReprocess?: (batch: IntakeBatch) => void
  /**
   * Id of the batch currently being reprocessed, or null/undefined when idle.
   * The matching row's Reprocess button shows a loading state and is disabled.
   */
  reprocessingBatchId?: string | null
  /** Base elm-id for testing selectors */
  elmId?: string
}
